import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SystemInterfaceType } from '../types';
import { loadFromLocalStorage, saveToLocalStorage } from '../utils/localStorageUtils';

const SYSTEM_INTERFACE_TYPE_KEY = 'yieldTrackApp_systemInterfaceType';

interface InterfaceContextType {
  interfaceType: SystemInterfaceType;
  setInterfaceType: (type: SystemInterfaceType) => void;
  isTouch: boolean;
  isWeb: boolean;
  selectedLineId: string | null;
  setSelectedLineId: (lineId: string | null) => void;
}

const InterfaceContext = createContext<InterfaceContextType | undefined>(undefined);

interface InterfaceProviderProps {
  children: ReactNode;
}

export const InterfaceProvider: React.FC<InterfaceProviderProps> = ({ children }) => {
  // Load interface type from localStorage, defaulting to WEB
  const [interfaceType, setInterfaceTypeState] = useState<SystemInterfaceType>(() =>
    loadFromLocalStorage(SYSTEM_INTERFACE_TYPE_KEY, SystemInterfaceType.WEB)
  );
  
  // State for selected line - will be initialized with first available line
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);

  // Initialize with first available line to prevent race condition
  useEffect(() => {
    const initializeSelectedLine = async () => {
      // Construct API URL
      const protocol = window.location.protocol;
      const hostname = window.location.hostname || 'localhost';
      const port = window.location.port ? `:${window.location.port}` : '';
      const apiUrl = `${protocol}//${hostname}${port}/api`;
      
      try {
        const response = await fetch(`${apiUrl}/lines`);
        const result = await response.json();
        
        if (result.message === 'success' && result.data && result.data.length > 0) {
          // Set the first line as default if none is selected
          if (!selectedLineId) {
            const firstLine = result.data[0];
            console.log('InterfaceContext: Initializing with first line:', firstLine.lineId, firstLine.name);
            setSelectedLineId(firstLine.lineId);
          }
        }
      } catch (error) {
        console.error('InterfaceContext: Error fetching lines for initialization:', error);
      }
    };

    initializeSelectedLine();
  }, []); // Run only once on mount

  // Save to localStorage whenever the type changes
  useEffect(() => {
    saveToLocalStorage(SYSTEM_INTERFACE_TYPE_KEY, interfaceType);
  }, [interfaceType]);

  const setInterfaceType = (type: SystemInterfaceType) => {
    setInterfaceTypeState(type);
  };

  // Computed properties for easy use in components
  const isTouch = interfaceType === SystemInterfaceType.TOUCH;
  const isWeb = interfaceType === SystemInterfaceType.WEB;

  const contextValue: InterfaceContextType = {
    interfaceType,
    setInterfaceType,
    isTouch,
    isWeb,
    selectedLineId,
    setSelectedLineId
  };

  return (
    <InterfaceContext.Provider value={contextValue}>
      <div className={isTouch ? 'touch-interface' : 'web-interface'}>
        {children}
      </div>
    </InterfaceContext.Provider>
  );
};

export const useInterface = (): InterfaceContextType => {
  const context = useContext(InterfaceContext);
  if (!context) {
    throw new Error('useInterface must be used within an InterfaceProvider');
  }
  return context;
};

// Hook to get touch-optimized styles
export const useTouchStyles = () => {
  const { isTouch } = useInterface();
  
  return {
    // Button styles
    button: isTouch ? 
      'px-6 py-4 text-lg min-h-[48px] touch-manipulation' : 
      'px-4 py-2 text-sm min-h-[36px]',
    
    // Input styles  
    input: isTouch ?
      'px-4 py-4 text-lg min-h-[48px] touch-manipulation' :
      'px-3 py-2 text-sm min-h-[36px]',
    
    // Clickable area styles
    clickable: isTouch ?
      'p-4 min-h-[48px] touch-manipulation' :
      'p-2 min-h-[36px]',
    
    // Table row styles
    tableRow: isTouch ?
      'h-16 touch-manipulation' :
      'h-12',
    
    // Modal styles
    modal: isTouch ?
      'p-6 text-lg' :
      'p-4 text-sm',
      
    // Font sizes
    fontSize: {
      small: isTouch ? 'text-base' : 'text-sm',
      medium: isTouch ? 'text-lg' : 'text-base', 
      large: isTouch ? 'text-xl' : 'text-lg'
    }
  };
};